import React, { useState, useEffect, useCallback } from "react";
import { Modal, Typography, Autocomplete, Button, SelectCard } from "@ann/web-components";
import {
  AssignRolesModalContent,
  RolesSearchRow,
  RolesSearchSummary,
  RolesSearchInputWrapper,
  ModalBodyNoPadding,
  RolesSelectionContainer,
  SelectedRolesInfo,
  SelectedRolesSummary,
  SelectedRolesTitle,
  SelectedRolesText,
  ClearSelectionText,
  LoadingContainer,
} from "./AssignRolesModal.styled";
import { getRoleList } from "../../services";
import useDebounce from "../../../../../hooks/useDebounce";
import { useBackendPagination } from "../../hooks/useBackendPagination";
import { LoadMoreSection } from "../../../DrivesPage/components/LoadMoreSection";

const AssignRolesModal = ({
  isOpen,
  onClose,
  searchValue,
  setSearchValue,
  selectedRoles,
  setSelectedRoles,
  onConfirmSelection,
}) => {
  // Debounce search value to avoid making requests on every keystroke
  const debouncedSearchValue = useDebounce(searchValue, 500);

  // Configure fetch function for getRoleList - memoized to avoid recreating on each render
  const fetchRoles = useCallback(async (params) => {
    try {
      const response = await getRoleList({
        page: params.page || 1,
        page_size: params.page_size || 8,
        order_by: params.order_by || ["created_at"],
        order_direction: params.order_direction || ["DESC"],
        search: params.search || null,
      });

      // Return in format expected by useBackendPagination
      return {
        body: response?.body || [],
        meta: response?.meta || {},
      };
    } catch (error) {
      console.error("Error fetching roles:", error);
      throw error;
    }
  }, []);

  // Use backend pagination hook
  const {
    items: allRoles,
    isLoading,
    error,
    hasMore,
    loadMore,
    reset,
    updateParams,
    meta,
  } = useBackendPagination(
    fetchRoles,
    {
      order_by: ["created_at"],
      order_direction: ["DESC"],
    },
    {
      itemsPerPage: 8,
      autoLoadFirstPage: false, // We'll control when to load
      onError: (error) => {
        console.error("Error loading roles:", error);
      },
    }
  );

  // Load roles when modal opens
  useEffect(() => {
    if (isOpen) {
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Update search params when search value changes (with debounce)
  useEffect(() => {
    if (!isOpen) return; // Don't search if modal is closed

    const timeoutId = setTimeout(() => {
      if (debouncedSearchValue && debouncedSearchValue.trim() !== "") {
        const keyword = debouncedSearchValue.trim();
        updateParams({ search: { keyword } });
        // Reset to page 1 when searching
        reset({ search: { keyword } });
      } else {
        updateParams({ search: undefined });
        // Reset to page 1 when clearing search
        reset({ search: undefined });
      }
    }, 0); // Already debounced, so no additional delay needed

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchValue, isOpen]);

  // Map API roles to modal format
  const roles = allRoles.map((role) => ({
    id: role.roleUuid,
    title: role.name,
    description: role.description || "",
    code: role.code,
  }));

  const totalRoles = meta?.total_items || roles.length;
  const visibleRoles = roles.length;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="small">
      <Modal.Header>
        <Modal.Header.Title>Assign roles</Modal.Header.Title>
      </Modal.Header>
      <ModalBodyNoPadding>
        <AssignRolesModalContent>
          <Typography category="body" size="medium" weight="regular">
            Assign roles to define what tools and features users can access. You can edit these functions in the next steps.
          </Typography>
          <RolesSearchRow>
            <RolesSearchInputWrapper>
              <Autocomplete
                value={searchValue}
                onChange={setSearchValue}
                onSearch={() => {}}
                onSuggestionSelect={() => {}}
                onClear={() => setSearchValue("")}
                placeholder="Search roles"
                suggestions={[]}
                disabled={isLoading}
              />
            </RolesSearchInputWrapper>
            <RolesSearchSummary>
              Showing {visibleRoles} of {totalRoles} roles
            </RolesSearchSummary>
          </RolesSearchRow>
          <RolesSelectionContainer>
            {isLoading ? (
              <LoadingContainer>
                <Typography category="body" size="medium" weight="regular" color="greyFour">
                  Loading roles...
                </Typography>
              </LoadingContainer>
            ) : (
              <>
                <SelectCard.Group
                  kind="checkbox"
                  value={selectedRoles}
                  onChange={setSelectedRoles}
                  direction="row"
                  itemsPerRow={2}
                >
                  {roles.map((role) => (
                    <SelectCard id={role.id} key={role.id}>
                      <SelectCard.Title value={role.title} />
                      <SelectCard.Content value={role.description} />
                    </SelectCard>
                  ))}
                </SelectCard.Group>
                {hasMore && (
                  <LoadMoreSection
                    hasMore={hasMore}
                    isLoading={false}
                    onLoadMore={loadMore}
                    skeletonType="folder"
                    skeletonCount={4}
                  />
                )}
              </>
            )}
          </RolesSelectionContainer>
          {selectedRoles.length > 0 && (
            <SelectedRolesSummary>
              <SelectedRolesInfo>
                <SelectedRolesTitle>
                  Selected roles ({selectedRoles.length})
                </SelectedRolesTitle>
                <SelectedRolesText>
                  {roles
                    .filter((role) => selectedRoles.includes(role.id))
                    .map((role) => role.title)
                    .join(" · ")}
                </SelectedRolesText>
              </SelectedRolesInfo>
              <ClearSelectionText type="button" onClick={() => setSelectedRoles([])} disabled={isLoading}>
                Clear Selection
              </ClearSelectionText>
            </SelectedRolesSummary>
          )}
        </AssignRolesModalContent>
      </ModalBodyNoPadding>
      <Modal.Actions align="end">
        <Button label="Cancel" onClick={onClose} type="secondary" size="medium" disabled={isLoading} />
        <Button
          label={`Add ${selectedRoles.length} roles`}
          onClick={() => {
            // Get complete role data for selected roles
            const selectedRolesData = roles.filter((role) => selectedRoles.includes(role.id));
            // Map to API format
            const rolesDataForStorage = selectedRolesData.map((role) => ({
              roleUuid: role.id,
              name: role.title,
              description: role.description,
              code: role.code,
            }));
            onConfirmSelection(selectedRoles, rolesDataForStorage);
            onClose();
          }}
          size="medium"
          type="primary"
          disabled={selectedRoles.length === 0 || isLoading}
        />
      </Modal.Actions>
    </Modal>
  );
};

export default AssignRolesModal;

